/** @module Review: the open cases as a queue, one decision at a time, every decision undoable. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Check, ChevronLeft, Ellipsis, Layers, LayoutGrid, RotateCcw, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { api, ApiError, type components, problemFrom } from "@/api/client";
import { CardFace } from "@/components/card-face";
import { ScanStatus, Segmented } from "@/components/ui/controls";
import { Sheet, useToast } from "@/components/ui/overlays";
import { ReviewCase, type ReviewMode } from "@/features/review/review-case";
import { type Approval, isTyping, type Outcome } from "@/features/review/use-review-case";
import { useCard } from "@/lib/cards";
import { useMediaQuery, WIDE } from "@/lib/media";
import { useSession } from "@/lib/use-session";

type Scan = components["schemas"]["ScanOut"];
type ScanDetail = components["schemas"]["ScanDetailOut"];
type Tab = "review" | "no_match";

/** The comparison stays the default; swipe is a choice each person makes once. */
const MODE_KEY = "poke-scan.review.mode";
/** A queue of two hundred names is a wall; the rest is counted below it. */
const QUEUE_SHOWN = 60;

/** Whose cases an administrator works on; everybody else only ever sees their own. */
const OWNER_KEY = "poke-scan.review.owner";

/** Deciding open cases, or releasing the labels testers already booked. */
type QueueKind = "decide" | "release";
const KIND_KEY = "poke-scan.review.kind";

async function fetchQueue(owner: string) {
  const { data, response } = await api.GET("/api/v1/review-queue", {
    params: { query: { limit: 200, owner } },
  });
  if (!data) throw problemFrom(response);
  return data;
}

/** Testers' labels waiting for an administrator; "me" and "all" both mean everybody here. */
async function fetchReleases(owner: string) {
  const userId = owner === "me" || owner === "all" ? undefined : owner;
  const { data, response } = await api.GET("/api/v1/admin/label-queue", {
    params: { query: { limit: 200, user_id: userId } },
  });
  if (!data) throw problemFrom(response);
  return data;
}

function readKind(): QueueKind {
  try {
    return localStorage.getItem(KIND_KEY) === "release" ? "release" : "decide";
  } catch {
    return "decide";
  }
}

/** The release queue keeps the decided card of the tester; the case starts from it. */
function approvalOf(scan: Scan): Approval | undefined {
  const resolution = (scan as ScanDetail).resolution;
  if (!resolution?.final_print_id) return undefined;
  return {
    printId: resolution.final_print_id,
    variantId: resolution.final_variant_id ?? null,
    language: resolution.final_language ?? null,
  };
}

function readOwner(): string {
  try {
    return localStorage.getItem(OWNER_KEY) ?? "me";
  } catch {
    return "me";
  }
}

/** Me, everybody, or one tester; the testers' scans no longer land in the admin's queue. */
function OwnerFilter({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  const { t } = useTranslation();
  const users = useQuery({
    queryKey: ["admin", "users"],
    queryFn: async () => {
      const { data, response } = await api.GET("/api/v1/admin/users", {});
      if (!data) throw problemFrom(response);
      return data;
    },
    staleTime: 5 * 60_000,
  });
  return (
    <label className="field">
      <span className="field-label">{t("review.owner")}</span>
      <select
        className="input"
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      >
        <option value="me">{t("review.ownerMe")}</option>
        <option value="all">{t("review.ownerAll")}</option>
        {(users.data ?? []).map((user) => (
          <option key={user.id} value={user.id}>
            {user.display_name}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Cases where two independent signals agree: the image search and the number read from
 * the card. Measured over 191 photos, those were right whenever the number was legible.
 */
export function safeCases(items: Scan[]): Scan[] {
  return items.filter(
    (scan) =>
      scan.result?.ocr_confirms === true &&
      Boolean(scan.result.chosen_print_id) &&
      Boolean(scan.result.chosen_variant_id),
  );
}

async function resolveScan(scan: Scan, language: string): Promise<void> {
  const result = scan.result;
  if (!result?.chosen_print_id || !result.chosen_variant_id) return;
  const { data, response } = await api.POST("/api/v1/scans/{scan_id}/resolve", {
    params: { path: { scan_id: scan.id } },
    body: {
      print_id: result.chosen_print_id,
      variant_id: result.chosen_variant_id,
      language: result.language_suggestion ?? language,
      condition: "NM",
    },
  });
  if (!data) {
    const problem = problemFrom(response);
    // Someone confirmed this one already; for a bulk run that is a success, not a failure.
    if (problem instanceof ApiError && problem.code === "scan_already_resolved") return;
    throw problem;
  }
}

function readMode(): ReviewMode {
  try {
    return localStorage.getItem(MODE_KEY) === "swipe" ? "swipe" : "compare";
  } catch {
    return "compare";
  }
}

function ConfirmAll({ items, onDone }: { items: Scan[]; onDone: () => void }) {
  const { t, i18n } = useTranslation();
  const [done, setDone] = useState(0);
  const safe = safeCases(items);

  const run = useMutation({
    mutationFn: async () => {
      setDone(0);
      for (const scan of safe) {
        await resolveScan(scan, i18n.resolvedLanguage ?? "de");
        setDone((count) => count + 1);
      }
    },
    onSuccess: onDone,
  });

  if (safe.length === 0) return null;
  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        data-testid="confirm-all"
        disabled={run.isPending}
        onClick={() => {
          run.mutate();
        }}
        className="btn btn-secondary btn-sm"
      >
        <Check />
        {t("review.confirmAll", { count: safe.length })}
      </button>
      <span className="t-xs t-muted">
        {run.isPending
          ? t("review.confirmingAll", { done, total: safe.length })
          : t("review.confirmAllHint")}
      </span>
      {run.isError ? (
        <span role="alert" className="t-xs loss">
          {t("review.error.generic")}
        </span>
      ) : null}
    </div>
  );
}

/** Why a case waits, in the words of the queue: "Kalibriermodus", "OCR passt nicht". */
function reasonOf(scan: Scan): string {
  const reasons = scan.result?.reasons ?? [];
  const order = [
    "calibration_mode",
    "ocr_does_not_confirm",
    "margin_too_small",
    "card_not_detected",
    "below_high_threshold",
    "below_low_threshold",
    "no_candidates",
  ];
  return order.find((reason) => reasons.includes(reason)) ?? "unclear";
}

function QueueItem({
  scan,
  current,
  onSelect,
}: {
  scan: Scan;
  current: boolean;
  onSelect: () => void;
}) {
  const { t, i18n } = useTranslation();
  const top = (scan.result?.candidates ?? [])[0] as { print_id?: string } | undefined;
  const card = useCard(top?.print_id ?? null);
  const parked = scan.result?.decision === "no_match";
  const count = (scan.result?.candidates ?? []).length;
  const time = new Date(scan.created_at).toLocaleTimeString(i18n.resolvedLanguage ?? "de", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return (
    <button type="button" className="q-item" aria-current={current} onClick={onSelect}>
      <ScanStatus state={parked ? "none" : "review"} />
      <span className="thumb">
        {top?.print_id ? <CardFace printId={top.print_id} name={card.data?.name} lazy /> : null}
      </span>
      <span className="flex min-w-0 grow flex-col">
        <b className="t-sm truncate">{card.data?.name ?? t("review.unknownCard")}</b>
        <span className="t-xs t-muted truncate">
          {reasonOf(scan) === "unclear"
            ? t("review.candidateCount", { count })
            : t(`review.reason.${reasonOf(scan)}`)}{" "}
          · {time}
        </span>
      </span>
    </button>
  );
}

export function ReviewPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const wide = useMediaQuery(WIDE);
  const admin = useSession().account?.role === "admin";
  const [owner, setOwnerState] = useState(() => (admin ? readOwner() : "me"));
  const [kind, setKindState] = useState<QueueKind>(() => (admin ? readKind() : "decide"));
  const releasing = kind === "release";
  const setKind = (next: QueueKind) => {
    setKindState(next);
    setSelectedId(null);
    setLastIndex(0);
    try {
      localStorage.setItem(KIND_KEY, next);
    } catch {
      // Starts on "decide" next time; not worth a message.
    }
  };
  // The count beside "Freigabe" also shows while deciding, so nothing piles up unseen.
  const waiting = useQuery({
    queryKey: ["review-queue", "release-count"],
    queryFn: async () => (await fetchReleases("all")).waiting,
    enabled: admin,
    refetchInterval: 60_000,
  });
  const setOwner = (next: string) => {
    setOwnerState(next);
    setSelectedId(null);
    setLastIndex(0);
    try {
      localStorage.setItem(OWNER_KEY, next);
    } catch {
      // Back to "me" next time; not worth a message.
    }
  };
  // Two people work the same list: what one of them settles has to vanish from the
  // other's screen by itself, otherwise the second one decides into the void.
  const queue = useQuery({
    queryKey: ["review-queue", kind, owner],
    queryFn: () => (releasing ? fetchReleases(owner) : fetchQueue(owner)),
    refetchInterval: 8000,
    refetchOnWindowFocus: true,
  });
  const [handled, setHandled] = useState<string[]>([]);
  const [takenByOther, setTakenByOther] = useState(0);
  // "/review?scan=…" opens one case directly, e.g. from the camera's correction sheet.
  const { scan: wanted } = useSearch({ strict: false });
  const [selectedId, setSelectedId] = useState<string | null>(wanted ?? null);
  // Settling a case keeps the place, so the same position shows the next one.
  const [lastIndex, setLastIndex] = useState(0);
  // Null until the person picks a side of the queue.
  const [chosenTab, setTab] = useState<Tab | null>(null);
  const [mode, setMode] = useState<ReviewMode>(readMode);
  const [menu, setMenu] = useState(false);

  const all = (queue.data?.items ?? []).filter((scan) => !handled.includes(scan.id));
  // A case asked for by link may be a parked one; the desktop queue then shows that side.
  const linked = all.find((scan) => scan.id === wanted);
  const tab: Tab = chosenTab ?? (linked?.result?.decision === "no_match" ? "no_match" : "review");
  const parked = all.filter((scan) => scan.result?.decision === "no_match");
  const ambiguous = all.filter((scan) => scan.result?.decision !== "no_match");
  // The desktop queue splits the two kinds; the phone works through all of them.
  // Released labels are all decided cards: no ambiguous and parked sides there.
  const items = wide && !releasing ? (tab === "review" ? ambiguous : parked) : all;
  const current =
    items.find((scan) => scan.id === selectedId) ??
    items[Math.min(lastIndex, Math.max(0, items.length - 1))];
  const index = current ? items.indexOf(current) : 0;

  const step = (by: number) => {
    if (items.length === 0) return;
    const next = items[(index + by + items.length) % items.length];
    setSelectedId(next?.id ?? null);
  };

  // Up and down walk the queue; left and right belong to the case itself.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey || isTyping(event.target)) return;
      if (document.querySelector("dialog[open]")) return;
      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();
        step(event.key === "ArrowUp" ? -1 : 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["review-queue"] });
    void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    void queryClient.invalidateQueries({ queryKey: ["collection"] });
  };

  const undo = async (scanId: string) => {
    const { response } = await api.POST("/api/v1/scans/{scan_id}/reopen", {
      params: { path: { scan_id: scanId } },
    });
    if (!response.ok) {
      toast.show({ text: t("review.undoFailed"), icon: <X /> }, 4000);
      return;
    }
    setHandled((current) => current.filter((id) => id !== scanId));
    setSelectedId(scanId);
    refresh();
    toast.show({ text: t("review.undone"), icon: <RotateCcw /> }, 2500);
  };

  const finish = (scan: Scan, outcome: Outcome) => {
    if (outcome.kind === "taken") setTakenByOther((count) => count + 1);
    setLastIndex(index);
    setSelectedId(null);
    // Hide it right away; waiting for the refetch invites a second tap on the same card.
    setHandled((current) => [...current, scan.id]);
    refresh();
    if (outcome.kind === "approved" || outcome.kind === "excluded") {
      toast.show(
        {
          text:
            outcome.kind === "approved"
              ? t("review.approved", { card: outcome.label ?? "" })
              : t("review.excluded"),
          icon: <ScanStatus state={outcome.kind === "approved" ? "auto" : "none"} />,
          placement: "top",
        },
        2500,
      );
    }
    if (outcome.kind === "confirmed" || outcome.kind === "dismissed") {
      toast.show({
        text:
          outcome.kind === "confirmed"
            ? t("review.booked", { card: outcome.label ?? "" })
            : t("review.dismissed"),
        icon: <ScanStatus state={outcome.kind === "confirmed" ? "auto" : "none"} />,
        action: {
          label: t("review.undo"),
          run: () => {
            void undo(scan.id);
          },
        },
        // The next card's buttons sit at the bottom; the toast must not cover them.
        placement: "top",
      });
    }
  };

  const chooseMode = (next: ReviewMode) => {
    setMode(next);
    try {
      localStorage.setItem(MODE_KEY, next);
    } catch {
      // The choice is a convenience; forgetting it is not an error.
    }
  };

  if (queue.isPending) return <p className="t-muted">{t("common.loading")}</p>;
  if (queue.isError)
    return (
      <p role="alert" className="loss">
        {t("common.loadError")}
      </p>
    );

  const confirmAll = (
    <ConfirmAll
      items={all}
      onDone={() => {
        setHandled((current) => [...current, ...safeCases(all).map((scan) => scan.id)]);
        refresh();
        setMenu(false);
      }}
    />
  );
  const ownerFilter = admin ? <OwnerFilter value={owner} onChange={setOwner} /> : null;
  const kindSwitch = admin ? (
    <Segmented
      label={t("review.kind")}
      value={kind}
      onChange={setKind}
      options={[
        { value: "decide", label: t("review.kindDecide") },
        {
          value: "release",
          label: t("review.kindRelease", { count: waiting.data ?? 0 }),
        },
      ]}
    />
  ) : null;
  const notice = (
    <>
      {takenByOther > 0 ? (
        <p className="callout callout-info">{t("review.takenByOther", { count: takenByOther })}</p>
      ) : null}
      {current?.owner && owner !== "me" ? (
        <p className="t-sm t-muted">{t("review.ownedBy", { name: current.owner.display_name })}</p>
      ) : null}
    </>
  );
  const empty = (
    <div className="flex flex-col items-start gap-3 py-6">
      <span className="row">
        <ScanStatus state="auto" />
        <b>{t("review.empty")}</b>
      </span>
    </div>
  );
  const theCase = current ? (
    <ReviewCase
      key={current.id}
      scan={current}
      mode={mode}
      wide={wide}
      approval={releasing ? approvalOf(current) : undefined}
      onDone={(outcome) => {
        finish(current, outcome);
      }}
    />
  ) : (
    empty
  );

  if (wide) {
    return (
      <div className="-mx-8 -mt-8 -mb-10 grid min-h-dvh grid-cols-[300px_minmax(0,1fr)]">
        <aside className="flex flex-col overflow-hidden border-r border-(--border) bg-(--surface)">
          <div className="flex flex-col gap-[10px] border-b border-(--border) p-4">
            <h1 className="t-h2">{t("nav.reviewQueue")}</h1>
            {kindSwitch}
            <Segmented
              className={releasing ? "hidden" : undefined}
              label={t("review.queueKind")}
              value={tab}
              onChange={(next) => {
                setTab(next);
                setSelectedId(null);
                setLastIndex(0);
              }}
              options={[
                {
                  value: "review",
                  label: t("review.ambiguousCount", { count: ambiguous.length }),
                },
                { value: "no_match", label: t("review.parkedCount", { count: parked.length }) },
              ]}
            />
            {ownerFilter}
            {releasing ? null : confirmAll}
          </div>
          <nav aria-label={t("review.navigation")} className="overflow-y-auto">
            {items.slice(0, QUEUE_SHOWN).map((scan) => (
              <QueueItem
                key={scan.id}
                scan={scan}
                current={scan.id === current?.id}
                onSelect={() => {
                  setSelectedId(scan.id);
                }}
              />
            ))}
            {items.length > QUEUE_SHOWN ? (
              <p className="t-xs t-muted p-4">
                {t("review.moreInQueue", { count: items.length - QUEUE_SHOWN })}
              </p>
            ) : null}
          </nav>
        </aside>
        <div className="flex min-w-0 flex-col gap-4 p-8">
          {notice}
          {theCase}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {/* The phone's app bar: back, where in the queue, and the rarer actions behind "…". */}
      <header className="-mx-4 -mt-4 grid min-h-[52px] grid-cols-[44px_1fr_44px] items-center px-2 py-2">
        <button
          type="button"
          className="icon-btn"
          aria-label={t("common.back")}
          onClick={() => {
            void navigate({ to: "/" });
          }}
        >
          <ChevronLeft />
        </button>
        <span className="flex flex-col items-center">
          <h1 className="text-[length:var(--text-base)] font-bold">{t("review.title")}</h1>
          {current ? (
            <span className="t-xs t-muted num">
              {t("review.position", { current: index + 1, total: items.length })}
            </span>
          ) : null}
        </span>
        <button
          type="button"
          className="icon-btn"
          aria-label={t("review.moreActions")}
          onClick={() => {
            setMenu(true);
          }}
        >
          <Ellipsis />
        </button>
      </header>
      <Segmented
        className="self-center"
        label={t("review.modeLabel")}
        value={mode}
        onChange={chooseMode}
        options={[
          { value: "compare", label: t("review.modeCompare"), icon: <LayoutGrid /> },
          { value: "swipe", label: t("review.modeSwipe"), icon: <Layers /> },
        ]}
      />
      {kindSwitch ? <div className="self-center">{kindSwitch}</div> : null}
      {notice}
      {theCase}
      <Sheet
        open={menu}
        onClose={() => {
          setMenu(false);
        }}
        label={t("review.moreActions")}
      >
        <div className="flex flex-col gap-4">
          <h2 className="t-h3">{t("review.moreActions")}</h2>
          {ownerFilter}
          {releasing ? null : confirmAll}
          <div className="row gap-2">
            <button
              type="button"
              className="btn btn-secondary grow"
              disabled={items.length < 2}
              onClick={() => {
                step(-1);
                setMenu(false);
              }}
            >
              {t("review.previous")}
            </button>
            <button
              type="button"
              className="btn btn-secondary grow"
              disabled={items.length < 2}
              onClick={() => {
                step(1);
                setMenu(false);
              }}
            >
              {t("review.next")}
            </button>
          </div>
        </div>
      </Sheet>
    </div>
  );
}
